// ════════════════════════════════════════════════════════════════════
// LandingArt — the product illustrations on the marketing pages.
//
// Drawn in code, not image files: each piece is laid out on a fixed design
// canvas (px) and scaled to its column by <Canvas>, so it stays crisp at any
// width and can follow the page's currency. Text inside the app "windows" is
// product UI (English, like a real screenshot); the floating cards around
// them take translated labels from the page. Platform tiles are plain
// wordmarks on the platform's colour, not their logo artwork.
//
// Motion hooks (CSS in SiteChrome.js): lp-pop (pop in once the section is
// revealed), lp-float (gentle bob), lp-par + --depth (mouse parallax in the
// hero), lp-grow (bars fill), lp-flow (data-flow dashes).
// ════════════════════════════════════════════════════════════════════
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { NAVY, ROYAL, BLUE, INK, ORANGE, prefersReducedMotion, fill } from "./SiteChrome";

const SLATE = "#5f6888";
const LINE = "#e6e9f2";
const HEAD = "'Plus Jakarta Sans', 'IBM Plex Sans Arabic', Inter, -apple-system, 'Segoe UI', sans-serif";
const BODY = "Inter, 'IBM Plex Sans Arabic', -apple-system, 'Segoe UI', sans-serif";

const at = (x, y, extra) => ({ position: "absolute", left: x, top: y, ...extra });
// Motion helpers: class list + CSS variables for one floating element.
const motion = (cls, d = 0, depth = 0) => ({ className: cls, vars: { "--d": d, "--depth": depth } });
// Translated card content: right-to-left for Arabic, start-aligned.
const Dir = ({ rtl, children, style }) => <div dir={rtl ? "rtl" : "ltr"} style={{ textAlign: "start", ...style }}>{children}</div>;

// ── Canvas: fixed design size, scaled to the available width ─────────────
export function Canvas({ w, h, label, children }) {
  const ref = useRef(null);
  const [scale, setScale] = useState(1);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const fit = () => setScale(Math.min(1, (el.clientWidth || w) / w));
    fit();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", fit);
      return () => window.removeEventListener("resize", fit);
    }
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
  }, [w]);
  return (
    <div ref={ref} role="img" aria-label={label}
      style={{ position: "relative", width: "100%", maxWidth: w, aspectRatio: `${w} / ${h}`, margin: "0 auto" }}>
      <div aria-hidden="true" dir="ltr"
        style={{ position: "absolute", top: 0, left: 0, width: w, height: h, transform: `scale(${scale})`, transformOrigin: "0 0",
          fontFamily: BODY, color: INK, pointerEvents: "none", userSelect: "none" }}>
        {children}
      </div>
    </div>
  );
}

// Counts up to `to` once mounted (the hero is on screen at load).
function CountUp({ to, delay = 700, dur = 1300 }) {
  const [v, setV] = useState(() => (prefersReducedMotion() ? to : 0));
  useEffect(() => {
    if (prefersReducedMotion() || typeof requestAnimationFrame === "undefined") {
      setV(to);
      return undefined;
    }
    let raf = 0;
    let start = null;
    const step = (ts) => {
      if (start === null) start = ts;
      const p = Math.min(1, (ts - start) / dur);
      setV(Math.round((to * (1 - Math.pow(1 - p, 3))) / 50) * 50);
      if (p < 1) raf = requestAnimationFrame(step);
    };
    const timer = setTimeout(() => { raf = requestAnimationFrame(step); }, delay);
    return () => {
      clearTimeout(timer);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [to, delay, dur]);
  return v.toLocaleString("en-US");
}

// ── Money: art numbers are written in GBP and scaled to look right in each
// market (not an exchange rate — real prices come from lib/pricing.js) ─────
const FX = {
  gbp: { rate: 1, pre: "£", loc: "en-GB", vat: 0.2 },
  aed: { rate: 4.6, pre: "AED ", loc: "en-AE", vat: 0.05 },
  inr: { rate: 28, pre: "₹", loc: "en-IN", vat: 0.18 },
};
function moneyFor(cur) {
  const fx = FX[cur] || FX.gbp;
  return (gbp, dp = 0) => {
    const v = gbp * fx.rate;
    const digits = cur === "inr" ? 0 : dp;
    const s = Math.abs(v).toLocaleString(fx.loc, { minimumFractionDigits: digits, maximumFractionDigits: digits });
    return `${v < 0 ? "−" : ""}${fx.pre}${s}`;
  };
}

// ── Platforms: tile colours + the line-up shown for each market ──────────
const PLAT = {
  deliveroo: { name: "Deliveroo", label: ["deliveroo"], bg: "#00ccbc", fg: "#fff", fs: 15 },
  talabat: { name: "Talabat", label: ["talabat"], bg: "#ff5a00", fg: "#fff", fs: 16 },
  ubereats: { name: "Uber Eats", label: ["Uber", "Eats"], bg: "#141414", fg: "#fff", fg2: "#06c167", fs: 17 },
  justeat: { name: "Just Eat", label: ["Just", "Eat"], bg: "#ff8000", fg: "#fff", fs: 17 },
  careem: { name: "Careem", label: ["careem"], bg: "#1dbf73", fg: "#fff", fs: 16 },
  noon: { name: "Noon", label: ["noon"], bg: "#feee00", fg: "#1a1a1a", fs: 19 },
  keeta: { name: "Keeta", label: ["Keeta"], bg: "#ffc72c", fg: "#1a1a1a", fs: 17 },
  swiggy: { name: "Swiggy", label: ["Swiggy"], bg: "#fc8019", fg: "#fff", fs: 16 },
  zomato: { name: "Zomato", label: ["zomato"], bg: "#e23744", fg: "#fff", fs: 16 },
};
const MARKET = {
  gbp: ["deliveroo", "ubereats", "justeat"],
  aed: ["talabat", "deliveroo", "careem"],
  inr: ["swiggy", "zomato"],
};
const marketOf = (cur) => MARKET[cur] || MARKET.gbp;

// ── Shared pieces ─────────────────────────────────────────────────────────
export function Tile({ p, x, y, size = 70, z = 3, className, vars }) {
  const t = PLAT[p];
  const k = size / 70;
  // Shrink long wordmarks (e.g. "deliveroo") so they fit inside the tile.
  const longest = Math.max(...t.label.map((l) => l.length));
  const fontPx = Math.min(t.fs * k, ((size - 6 * k) * 0.92) / (longest * 0.55));
  return (
    <div className={className} style={at(x, y, { width: size, height: size, borderRadius: 17 * k, background: t.bg, border: `${3.5 * k}px solid #fff`, zIndex: z,
      boxShadow: "0 14px 28px -12px rgba(15,27,77,.45), 0 0 0 1px rgba(15,27,77,.05)", display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center", fontFamily: HEAD, fontWeight: 800, letterSpacing: longest > 7 ? "-.8px" : "-.4px", lineHeight: 0.95, textAlign: "center", ...vars })}>
      {t.label.map((l, i) => (
        <span key={l} style={{ color: i === 1 && t.fg2 ? t.fg2 : t.fg, fontSize: fontPx }}>{l}</span>
      ))}
    </div>
  );
}

function Rings({ cx, cy, r, n = 5 }) {
  const fills = ["#f4f6ff", "#ebf0ff", "#e1e8ff", "#d7e0fe", "#ccd7fd", "#c2cffc"];
  const step = r / (n + 1.4);
  return (
    <svg style={at(cx - r, cy - r, { zIndex: 0 })} width={r * 2} height={r * 2} viewBox={`0 0 ${r * 2} ${r * 2}`}>
      {Array.from({ length: n }, (_, i) => (
        <circle key={i} cx={r} cy={r} r={r - 1 - i * step} fill={fills[i]} stroke={BLUE} strokeOpacity={0.45} strokeWidth={1.2} />
      ))}
    </svg>
  );
}

function Orb({ x, y, d }) {
  return (
    <div className="lp-pop" style={at(x, y, { width: d, height: d, borderRadius: "50%", zIndex: 0, "--d": 0,
      background: `radial-gradient(circle at 70% 26%, #8aa2ff 0%, ${ROYAL} 36%, ${NAVY} 92%)`,
      boxShadow: "0 50px 90px -40px rgba(20,34,110,.6)" })} />
  );
}

function Card({ x, y, w, h, children, z = 4, pad = 14, style, className, vars }) {
  return (
    <div className={className} style={at(x, y, { width: w, height: h, background: "#fff", borderRadius: 14, padding: pad, zIndex: z,
      boxShadow: "0 24px 48px -20px rgba(15,27,77,.38), 0 0 0 1px rgba(15,27,77,.06)", ...style, ...vars })}>
      {children}
    </div>
  );
}

function Chip({ tone = "blue", children, style }) {
  const tones = {
    blue: ["#e6ebff", BLUE], green: ["#dcfce7", "#15803d"], red: ["#fee2e2", "#b91c1c"],
    orange: ["#ffedd5", "#c2410c"], slate: ["#eef1f6", "#475569"], navy: [NAVY, "#fff"],
  };
  const [bg, fg] = tones[tone];
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 8px", borderRadius: 999, background: bg,
      color: fg, fontSize: 10.5, fontWeight: 700, whiteSpace: "nowrap", lineHeight: 1.4, ...style }}>{children}</span>
  );
}

const SIDE_ICONS = [
  "M3 11l9-7 9 7M5 9.5V20h14V9.5",
  "M5 20V11M12 20V5M19 20v-6",
  "M4 6h16M4 12h16M4 18h10",
  "M20 13.5 13.5 20 4 10.5V4h6.5z",
  "M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9z",
];

// macOS-style app window with the Netrev sidebar.
function Win({ x, y, w, h, title, sub, active = 0, sidebar = true, children, z = 2, className, vars }) {
  return (
    <div className={className} style={at(x, y, { width: w, height: h, background: "#eceef5", borderRadius: 16, padding: "28px 8px 8px", zIndex: z,
      boxShadow: "0 34px 64px -28px rgba(15,27,77,.42), 0 0 0 1px rgba(15,27,77,.07)", ...vars })}>
      <div style={at(14, 9, { display: "flex", gap: 6 })}>
        {[0, 1, 2].map((i) => <span key={i} style={{ width: 10, height: 10, borderRadius: "50%", border: "1.5px solid #c4c9d8", background: "#f7f8fb" }} />)}
      </div>
      <div style={{ display: "flex", height: "100%", background: "#f6f7fb", borderRadius: 10, overflow: "hidden" }}>
        {sidebar && (
          <div style={{ width: 44, flexShrink: 0, background: "#fff", borderRight: `1px solid ${LINE}`, display: "flex", flexDirection: "column", alignItems: "center", paddingTop: 10, gap: 10 }}>
            <img src="/logo.svg" alt="" width={26} height={26} style={{ borderRadius: 7, display: "block", marginBottom: 4 }} />
            {SIDE_ICONS.map((d, i) => (
              <div key={d} style={{ width: 28, height: 28, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", background: i === active ? "#dde5ff" : "transparent" }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={i === active ? BLUE : "#8a94ad"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d={d} /></svg>
              </div>
            ))}
          </div>
        )}
        <div style={{ flex: 1, minWidth: 0, padding: "13px 16px" }}>
          {title && <div style={{ fontFamily: HEAD, fontWeight: 800, fontSize: 21, letterSpacing: "-.5px", lineHeight: 1.1, color: INK }}>{title}</div>}
          {sub && <div style={{ fontSize: 10.5, color: SLATE, marginTop: 3, fontWeight: 500 }}>{sub}</div>}
          <div style={{ marginTop: title ? 11 : 0 }}>{children}</div>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, color = INK }) {
  return (
    <div style={{ flex: 1, minWidth: 0, background: "#fff", border: `1px solid ${LINE}`, borderRadius: 9, padding: "8px 10px" }}>
      <div style={{ fontSize: 9, fontWeight: 700, color: SLATE, letterSpacing: 0.3, whiteSpace: "nowrap" }}>{label}</div>
      <div style={{ fontSize: 14.5, fontWeight: 800, color, letterSpacing: "-.3px", marginTop: 3, whiteSpace: "nowrap" }}>{value}</div>
    </div>
  );
}

const Spark = ({ size = 11, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color}><path d="M12 2l2.4 6.6L21 11l-6.6 2.4L12 20l-2.4-6.6L3 11l6.6-2.4z" /></svg>
);
const Tick = ({ size = 10, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
);
const Cross = ({ size = 10, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="3.4" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
);

// The story card: one week at our own restaurant, POS report vs bank.
// `k` scales the type for the compact (phone) hero.
function GapCard({ x, y, w = 232, gap, rtl, d = 2, depth = 16, k = 1 }) {
  const m = motion("lp-pop lp-par", d, depth);
  const small = { fontSize: 10.5 * k, color: SLATE, fontWeight: 600 };
  return (
    <Card x={x} y={y} w={w} z={6} pad={15 * k} {...m}>
      <Dir rtl={rtl}>
        <div style={{ ...small, fontWeight: 700, letterSpacing: 0.2 }}>{gap.eyebrow}</div>
        <div style={{ ...small, marginTop: 9 * k }}>{gap.pos}</div>
        <div style={{ fontFamily: HEAD, fontSize: 23 * k, fontWeight: 800, letterSpacing: "-.6px", lineHeight: 1.15 }}><bdi dir="ltr"><CountUp to={30000} /> AED</bdi></div>
        <div style={{ ...small, marginTop: 6 * k }}>{gap.bank}</div>
        <div style={{ fontFamily: HEAD, fontSize: 28 * k, fontWeight: 800, letterSpacing: "-.8px", lineHeight: 1.1, color: ORANGE }}><bdi dir="ltr"><CountUp to={14500} delay={900} /> AED</bdi></div>
        <div dir="ltr" style={{ display: "flex", height: 9 * k, borderRadius: 5, overflow: "hidden", marginTop: 10 * k, background: "repeating-linear-gradient(135deg,#e3e7f1 0 4px,#cfd5e4 4px 7px)" }}>
          <div className="lp-grow" style={{ width: "48.3%", background: ORANGE, "--d": 2 }} />
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 8, marginTop: 6 * k, fontSize: 10.5 * k, fontWeight: 700 }}>
          <span style={{ color: SLATE }}>{gap.deducted}</span>
          <bdi dir="ltr" style={{ color: "#c2410c", whiteSpace: "nowrap" }}>−15,500 AED</bdi>
        </div>
      </Dir>
    </Card>
  );
}

function TrackedCard({ x, y, w, tracked, ded, rtl, k = 1, d = 5, depth = 12 }) {
  return (
    <Card x={x} y={y} w={w} z={6} pad={13 * k} {...motion("lp-pop lp-par", d, depth)}>
      <Dir rtl={rtl}>
        <div style={{ fontSize: 12 * k, fontWeight: 800, marginBottom: 8 * k }}>{tracked}</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 5 * k }}>
          {ded.map((t) => <Chip key={t} tone="blue" style={{ fontSize: 10.5 * k, padding: `${3 * k}px ${8 * k}px` }}><Tick size={9 * k} />{t}</Chip>)}
        </div>
      </Dir>
    </Card>
  );
}

// ════════════════════════════════════════════════════════════════════
// HERO — the POS-vs-bank story card over an Insight+ window (AED: the story
// is our own UAE restaurant's week, exactly as in the partner deck).
// `text` = { gap, tracked, ded, ai: { tag, title, action, impact } }.
// ════════════════════════════════════════════════════════════════════
export function HeroArt({ label, text, rtl }) {
  return (
    <Canvas w={620} h={556} label={label}>
      <Orb x={196} y={46} d={420} />

      <Win x={18} y={150} w={468} h={296} title="Insight+" sub="bistro47 · Dubai · This month" active={0} {...motion("lp-pop lp-par", 1, 6)}>
        <div style={{ display: "flex", gap: 8 }}>
          <Stat label="GROSS SALES" value="AED 128,400" />
          <Stat label="NET RECEIVABLE" value="AED 66,800" color={BLUE} />
          <Stat label="TRUE PROFIT" value="AED 21,800" color="#0b8a5f" />
        </div>
        <div style={{ marginTop: 12, background: "#fff", border: `1px solid ${LINE}`, borderRadius: 10, padding: "9px 12px" }}>
          <div style={{ fontSize: 11, fontWeight: 800, marginBottom: 6 }}>Profit by platform</div>
          {[["Deliveroo", 24, "#00ccbc"], ["Talabat", 19, "#ff5a00"], ["Careem", 15, "#1dbf73"], ["Noon", -6, "#e0c800"]].map(([n, mg, c], i) => (
            <div key={n} style={{ display: "flex", alignItems: "center", gap: 8, height: 21 }}>
              <span style={{ width: 8, height: 8, borderRadius: 2, background: c, flexShrink: 0 }} />
              <span style={{ width: 62, fontSize: 11, fontWeight: 600 }}>{n}</span>
              <div style={{ flex: 1, height: 7, borderRadius: 4, background: "#eef1f7", overflow: "hidden" }}>
                <div className="lp-grow" style={{ "--d": i + 2, width: `${Math.max(4, Math.abs(mg) * 3.4)}%`, height: "100%", borderRadius: 4, background: mg < 0 ? "#ef4444" : `linear-gradient(90deg, ${NAVY}, ${ROYAL})` }} />
              </div>
              <Chip tone={mg < 0 ? "red" : "green"} style={{ minWidth: 40, justifyContent: "center" }}>{mg < 0 ? "−" : ""}{Math.abs(mg)}%</Chip>
            </div>
          ))}
        </div>
      </Win>

      <GapCard x={376} y={14} gap={text.gap} rtl={rtl} d={3} depth={18} />
      <TrackedCard x={0} y={424} w={252} tracked={text.tracked} ded={text.ded} rtl={rtl} />

      <Card x={372} y={414} w={236} z={6} pad={13} {...motion("lp-pop lp-par", 6, 14)}>
        <Dir rtl={rtl}>
          <Chip tone="navy"><Spark size={10} color="#fff" />{text.ai.tag}</Chip>
          <div style={{ fontSize: 12.5, fontWeight: 800, marginTop: 7, lineHeight: 1.35 }}>{text.ai.title}</div>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 7, flexWrap: "wrap" }}>
            <span style={{ fontSize: 10.5, color: SLATE, fontWeight: 600 }}>{text.ai.action}</span>
            <Chip tone="green">{fill(text.ai.impact, { price: "+AED 2,100" })}</Chip>
          </div>
        </Dir>
      </Card>

      <Tile p="keeta" x={0} y={232} size={66} {...motion("lp-pop lp-float lp-par", 4, 24)} />
      <Tile p="talabat" x={132} y={86} size={70} {...motion("lp-pop lp-float lp-par", 2, 26)} />
      <Tile p="deliveroo" x={256} y={50} size={80} {...motion("lp-pop lp-float lp-par", 3, 30)} />
      <Tile p="careem" x={540} y={238} size={66} {...motion("lp-pop lp-float lp-par", 5, 22)} />
      <Tile p="noon" x={284} y={472} size={62} {...motion("lp-pop lp-float lp-par", 7, 20)} />
    </Canvas>
  );
}

// Phone version of the hero: just the story, large enough to read.
export function HeroArtCompact({ label, text, rtl }) {
  return (
    <Canvas w={400} h={430} label={label}>
      <Orb x={72} y={30} d={300} />
      <GapCard x={36} y={64} w={328} gap={text.gap} rtl={rtl} d={2} depth={0} k={1.38} />
      <TrackedCard x={18} y={318} w={364} tracked={text.tracked} ded={text.ded} rtl={rtl} k={1.25} d={4} depth={0} />
      <Tile p="talabat" x={6} y={10} size={58} {...motion("lp-pop lp-float", 1)} />
      <Tile p="deliveroo" x={318} y={6} size={62} {...motion("lp-pop lp-float", 3)} />
    </Canvas>
  );
}

// ════════════════════════════════════════════════════════════════════
// INSIGHT+ — profit by platform for the market's platforms.
// `text` = { weekDeductions, ded: [commission, vat, card, cpc, subscription] }.
// ════════════════════════════════════════════════════════════════════
const PL_ROWS = [
  { gross: 12480, net: 8236, profit: 3120 },
  { gross: 9760, net: 6345, profit: 2050 },
  { gross: 6210, net: 3920, profit: -310 },
];
export function InsightArt({ label, cur, text, rtl }) {
  const m = moneyFor(cur);
  const plats = marketOf(cur);
  const rows = plats.map((p, i) => ({ p, ...PL_ROWS[i] }));
  const total = rows.reduce((a, r) => ({ gross: a.gross + r.gross, net: a.net + r.net, profit: a.profit + r.profit }), { gross: 0, net: 0, profit: 0 });
  const xs = plats.length === 3 ? [104, 246, 388] : [175, 317];
  // Commission ≈ 30% of net ticket; with VAT on it, card fees, CPC and the
  // platform subscription the lines add up to gross minus net receivable.
  const comm = 6862;
  const amounts = [-comm, -Math.round(comm * (FX[cur] || FX.gbp).vat), -427, -1138, -150];
  const cols = "122px 70px 92px 76px 54px";
  const cell = (v, extra) => <div style={{ textAlign: "right", whiteSpace: "nowrap", ...extra }}>{v}</div>;
  return (
    <Canvas w={560} h={510} label={label}>
      <Rings cx={280} cy={286} r={210} />
      {plats.map((p, i) => <Tile key={p} p={p} x={xs[i]} y={20} size={68} {...motion("lp-pop lp-float", i + 1)} />)}

      <Win x={16} y={116} w={528} h={262} title="Profit by platform" sub="All platforms · This week" active={1} {...motion("lp-pop", 0)}>
        <div style={{ background: "#fff", border: `1px solid ${LINE}`, borderRadius: 10, overflow: "hidden", fontSize: 11 }}>
          <div style={{ display: "grid", gridTemplateColumns: cols, padding: "7px 10px", background: "#f8f9fc", borderBottom: `1px solid ${LINE}`, fontSize: 9, fontWeight: 700, color: SLATE, letterSpacing: 0.3 }}>
            <div>PLATFORM</div>{cell("GROSS")}{cell("NET RECEIVABLE")}{cell("TRUE PROFIT")}{cell("MARGIN")}
          </div>
          {rows.map((r) => (
            <div key={r.p} style={{ display: "grid", gridTemplateColumns: cols, alignItems: "center", padding: "0 10px", height: 29, borderBottom: "1px solid #f0f2f7" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 7, fontWeight: 700 }}>
                <span style={{ width: 9, height: 9, borderRadius: 3, background: PLAT[r.p].bg }} />{PLAT[r.p].name}
              </div>
              {cell(m(r.gross), { color: "#4b5675" })}
              {cell(m(r.net), { color: BLUE, fontWeight: 700 })}
              {cell(m(r.profit), { color: r.profit < 0 ? "#dc2626" : "#0b8a5f", fontWeight: 800 })}
              {cell(<Chip tone={r.profit < 0 ? "red" : "green"}>{Math.round((r.profit / r.gross) * 100)}%</Chip>)}
            </div>
          ))}
          <div style={{ display: "grid", gridTemplateColumns: cols, alignItems: "center", padding: "0 10px", height: 31, fontWeight: 800, background: "#fbfcfe" }}>
            <div>Total</div>
            {cell(m(total.gross))}{cell(m(total.net), { color: BLUE })}{cell(m(total.profit), { color: "#0b8a5f" })}
            {cell(<Chip tone="blue">{Math.round((total.profit / total.gross) * 100)}%</Chip>)}
          </div>
        </div>
      </Win>

      <Card x={326} y={356} w={224} z={6} pad={13} {...motion("lp-pop", 4)}>
        <Dir rtl={rtl}>
          <div style={{ fontSize: 11.5, fontWeight: 800, marginBottom: 6 }}>{text.weekDeductions}</div>
          {text.ded.map((n, i) => (
            <div key={n} style={{ display: "flex", justifyContent: "space-between", gap: 10, fontSize: 11, height: 20, alignItems: "center" }}>
              <span style={{ color: "#4b5675", fontWeight: 600, whiteSpace: "nowrap" }}>{n}</span>
              <bdi dir="ltr" style={{ color: "#c2410c", fontWeight: 700 }}>{m(amounts[i])}</bdi>
            </div>
          ))}
        </Dir>
      </Card>
    </Canvas>
  );
}

// ════════════════════════════════════════════════════════════════════
// AI ANALYTICS — inputs → Netrev AI → recommended actions.
// `text` = { bubbleIn, bubbleOut, toggles[6], tags{cpc,discount,platform},
//            rec{cpc,discount,grow,losing}, perMonth }.
// ════════════════════════════════════════════════════════════════════
function Bubble({ x, y, w, children, d, rtl }) {
  return (
    <div className="lp-pop" style={at(x, y, { width: w, zIndex: 5, "--d": d })}>
      <div dir={rtl ? "rtl" : "ltr"} style={{ background: NAVY, color: "#fff", borderRadius: 10, padding: "9px 12px", fontFamily: HEAD, fontWeight: 700, fontSize: 13, textAlign: "center", boxShadow: "0 10px 20px -10px rgba(20,34,110,.6)" }}>{children}</div>
      <div style={{ width: 12, height: 12, background: NAVY, transform: "rotate(45deg)", margin: "-7px auto 0" }} />
    </div>
  );
}
export function AiArt({ label, cur, text, rtl }) {
  const m = moneyFor(cur);
  const [p0, p1, p2] = marketOf(cur).map((p) => PLAT[p].name);
  const losing = p2 || p1;
  const colors = [NAVY, NAVY, NAVY, BLUE, BLUE, NAVY];
  const recs = [
    { tone: "blue", tag: text.tags.cpc, top: BLUE, title: fill(text.rec.cpc, { p: losing }), impact: <Chip tone="green">{fill(text.perMonth, { price: `+${m(620)}` })}</Chip> },
    { tone: "orange", tag: text.tags.discount, top: ORANGE, title: fill(text.rec.discount, { p: p1 }), impact: <Chip tone="red">{fill(text.rec.losing, { price: m(0.4, 2) })}</Chip> },
    { tone: "green", tag: text.tags.platform, top: "#0ca678", title: fill(text.rec.grow, { p: p0 }), impact: <Chip tone="green">{fill(text.perMonth, { price: `+${m(1480)}` })}</Chip> },
  ];
  const dash = { stroke: "#7d89ad", strokeWidth: 2, strokeDasharray: "3 5", fill: "none", strokeLinecap: "round", className: "lp-flow" };
  return (
    <Canvas w={560} h={500} label={label}>
      <Rings cx={290} cy={262} r={238} />
      <div className="lp-pop" style={at(10, 96, { width: 420, height: 362, background: "#eceef5", borderRadius: 16, padding: 9, zIndex: 1, "--d": 0, boxShadow: "0 30px 60px -30px rgba(15,27,77,.4), 0 0 0 1px rgba(15,27,77,.06)" })}>
        <div style={{ width: "100%", height: "100%", background: "#f6f7fb", borderRadius: 10 }} />
      </div>
      <Bubble x={22} y={14} w={182} d={1} rtl={rtl}>{text.bubbleIn}</Bubble>
      <Bubble x={354} y={14} w={192} d={6} rtl={rtl}>{text.bubbleOut}</Bubble>

      {text.toggles.map((n, i) => (
        <div key={n} className="lp-pop" style={at(26, 112 + i * 56, { width: 178, height: 48, zIndex: 3, "--d": i + 1, background: "#fff", borderRadius: 10, border: `1px solid ${LINE}`, display: "flex", alignItems: "center", gap: 11, padding: "0 12px", boxShadow: "0 2px 6px rgba(15,27,77,.04)" })}>
          <span style={{ width: 38, height: 22, borderRadius: 11, background: colors[i], position: "relative", flexShrink: 0 }}>
            <span style={{ position: "absolute", right: 3, top: 3, width: 16, height: 16, borderRadius: "50%", background: "#fff" }} />
          </span>
          <span dir={rtl ? "rtl" : "ltr"} style={{ fontSize: 12.5, fontWeight: 700, whiteSpace: "nowrap" }}>{n}</span>
        </div>
      ))}

      <svg style={at(0, 0, { zIndex: 2 })} width={560} height={500}>
        <defs>
          <linearGradient id="lp-ai-chip" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor={NAVY} /><stop offset="1" stopColor={ROYAL} />
          </linearGradient>
        </defs>
        {/* inputs → chip */}
        <path d="M206 136 H224 V416 H206" {...dash} />
        <path d="M224 276 H232" {...dash} />
        <path d="M232 270 l8 6 -8 6" fill="#7d89ad" />
        {/* chip → recommendations */}
        <path d="M330 276 H336" {...dash} />
        <path d="M336 150 V410" {...dash} />
        {[150, 280, 410].map((y) => (
          <g key={y}>
            <path d={`M336 ${y} H339`} {...dash} />
            <path d={`M339 ${y - 6} l8 6 -8 6`} fill="#7d89ad" />
          </g>
        ))}
        {/* Netrev AI chip: three pins per side, centred on a 58px die */}
        <text x="285" y="222" textAnchor="middle" fontFamily={HEAD} fontWeight="800" fontSize="19" letterSpacing="-.6" fill={INK}>Netrev</text>
        <g className="lp-pop" style={{ transformBox: "fill-box", transformOrigin: "center", "--d": 5 }}>
          <g stroke={NAVY} strokeWidth="3" strokeLinecap="round">
            {[-15, 0, 15].map((o) => (
              <g key={o}>
                <line x1={285 + o} y1="247" x2={285 + o} y2="238" />
                <line x1={285 + o} y1="305" x2={285 + o} y2="314" />
                <line x1="256" y1={276 + o} x2="247" y2={276 + o} />
                <line x1="314" y1={276 + o} x2="323" y2={276 + o} />
              </g>
            ))}
          </g>
          <g fill={NAVY}>
            {[-15, 0, 15].map((o) => (
              <g key={o}>
                <circle cx={285 + o} cy="235" r="3" /><circle cx={285 + o} cy="317" r="3" />
                <circle cx="244" cy={276 + o} r="3" /><circle cx="326" cy={276 + o} r="3" />
              </g>
            ))}
          </g>
          <rect x="256" y="247" width="58" height="58" rx="13" fill="url(#lp-ai-chip)" />
          <rect x="262" y="253" width="46" height="46" rx="9" fill="none" stroke="#fff" strokeOpacity=".22" strokeWidth="1.5" />
          <text x="285" y="283.5" textAnchor="middle" fontFamily={HEAD} fontWeight="800" fontSize="21" fill="#fff">AI</text>
        </g>
      </svg>

      {recs.map((r, i) => (
        <Card key={r.top} x={346} y={94 + i * 130} w={204} h={112} z={6} pad={12} style={{ borderTop: `4px solid ${r.top}` }} {...motion("lp-pop", i + 7)}>
          <Dir rtl={rtl}>
            <Chip tone={r.tone}>{r.tag}</Chip>
            <div style={{ fontSize: 12.5, fontWeight: 800, lineHeight: 1.3, marginTop: 7, minHeight: 32 }}>{r.title}</div>
            <div style={{ marginTop: 6 }}>{r.impact}</div>
          </Dir>
        </Card>
      ))}
    </Canvas>
  );
}

// ════════════════════════════════════════════════════════════════════
// PRICE TOOL — profit per item after discount and campaign fee, plus a
// campaign check. Columns mirror the real Price Tool.
// `text` = { campaign, profitPerOrder, loses, discount, campaignFee }.
// ════════════════════════════════════════════════════════════════════
const MENU = [
  ["Chicken burger", 11.5, 10, 0.4, 3.71],
  ["Margherita pizza", 13, 20, 0.6, 2.84],
  ["Caesar salad", 8.5, 30, 0.5, -0.38],
  ["Loaded fries", 5.5, 50, 0.5, -1.12],
  ["Chocolate brownie", 4.5, 0, 0, 1.62],
];
export function PriceArt({ label, cur, text, rtl }) {
  const m = moneyFor(cur);
  const plats = marketOf(cur);
  const p0 = plats[0];
  const p1 = plats[1];
  const cols = "124px 60px 64px 76px 82px";
  const cell = (v, extra) => <div style={{ textAlign: "right", whiteSpace: "nowrap", ...extra }}>{v}</div>;
  const row = (labelText, value, color) => (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 10, fontSize: 11.5, height: 22, alignItems: "center" }}>
      <span style={{ color: "#4b5675", fontWeight: 600 }}>{labelText}</span>
      <bdi dir="ltr" style={{ color, fontWeight: 800 }}>{value}</bdi>
    </div>
  );
  return (
    <Canvas w={560} h={500} label={label}>
      <Rings cx={290} cy={284} r={208} />
      <Win x={18} y={104} w={520} h={282} title="Price Tool" sub={`${PLAT[p0].name} · Menu · 17 items`} active={3} {...motion("lp-pop", 0)}>
        <div style={{ background: "#fff", border: `1px solid ${LINE}`, borderRadius: 10, overflow: "hidden", fontSize: 11 }}>
          <div style={{ display: "grid", gridTemplateColumns: cols, padding: "7px 10px", background: "#f8f9fc", borderBottom: `1px solid ${LINE}`, fontSize: 9, fontWeight: 700, color: SLATE, letterSpacing: 0.3 }}>
            <div>ITEM</div>{cell("PRICE")}{cell("DISCOUNT")}{cell("CAMPAIGN FEE")}{cell("PROFIT")}
          </div>
          {MENU.map(([n, price, disc, fee, profit]) => (
            <div key={n} style={{ display: "grid", gridTemplateColumns: cols, alignItems: "center", padding: "0 10px", height: 30, borderBottom: "1px solid #f0f2f7" }}>
              <div style={{ fontWeight: 700 }}>{n}</div>
              {cell(m(price, 2), { color: "#4b5675" })}
              {cell(`${disc}%`, { color: disc >= 30 ? "#c2410c" : BLUE, fontWeight: 700 })}
              {cell(m(-fee, 2), { color: "#4b5675" })}
              {cell(<Chip tone={profit < 0 ? "red" : "green"}>{profit < 0 ? "" : "+"}{m(profit, 2)}</Chip>)}
            </div>
          ))}
        </div>
      </Win>

      <Card x={330} y={10} w={222} z={6} pad={13} {...motion("lp-pop lp-float", 3)}>
        <Dir rtl={rtl}>
          <Chip tone="orange">{fill(text.campaign, { p: PLAT[p1].name })}</Chip>
          <div style={{ fontSize: 10.5, color: SLATE, fontWeight: 600, marginTop: 9 }}>{text.profitPerOrder}</div>
          <div dir="ltr" style={{ display: "flex", alignItems: "baseline", gap: 8, marginTop: 2, justifyContent: rtl ? "flex-end" : "flex-start" }}>
            <span style={{ fontSize: 14, color: "#9aa3bb", fontWeight: 700, textDecoration: "line-through" }}>{m(3.2, 2)}</span>
            <span style={{ fontFamily: HEAD, fontSize: 22, color: "#dc2626", fontWeight: 800, letterSpacing: "-.5px" }}>{m(-0.4, 2)}</span>
          </div>
          <div style={{ marginTop: 7 }}><Chip tone="red">{text.loses}</Chip></div>
        </Dir>
      </Card>

      <Card x={6} y={360} w={236} z={6} pad={13} {...motion("lp-pop", 5)}>
        <Dir rtl={rtl}>
          <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 6 }}>Caesar salad · {PLAT[p0].name}</div>
          {row(text.discount, "30%", "#c2410c")}
          {row(text.campaignFee, m(-0.5, 2), "#4b5675")}
          {row(text.profitPerOrder, m(-0.38, 2), "#dc2626")}
        </Dir>
      </Card>

      <Tile p={p1} x={240} y={24} size={62} {...motion("lp-pop lp-float", 2)} />
      <Tile p={p0} x={472} y={418} size={66} {...motion("lp-pop lp-float", 6)} />
    </Canvas>
  );
}

// ════════════════════════════════════════════════════════════════════
// PARTNERS — two-way integration: sales in, true net back.
// ════════════════════════════════════════════════════════════════════
export function PartnerArt({ label, text, rtl }) {
  return (
    <Canvas w={560} h={500} label={label}>
      <Rings cx={280} cy={262} r={228} />
      <svg style={at(0, 0, { zIndex: 1 })} width={560} height={500}>
        <line x1={56} y1={96} x2={56} y2={160} stroke="#00ccbc" strokeWidth={2.5} strokeDasharray="4 4" className="lp-flow" />
        <line x1={144} y1={96} x2={144} y2={160} stroke="#ff5a00" strokeWidth={2.5} strokeDasharray="4 4" className="lp-flow" />
        <path d="M200 236 H326" stroke={BLUE} strokeWidth={2.8} strokeDasharray="6 6" fill="none" className="lp-flow" />
        <path d="M324 227 l14 9 -14 9z" fill={BLUE} />
        <path d="M214 312 H340" stroke={ORANGE} strokeWidth={2.8} strokeDasharray="6 6" fill="none" className="lp-flow lp-flow-rev" />
        <path d="M216 303 l-14 9 14 9z" fill={ORANGE} />
      </svg>
      <Tile p="deliveroo" x={24} y={30} size={64} {...motion("lp-pop lp-float", 1)} />
      <Tile p="talabat" x={112} y={30} size={64} {...motion("lp-pop lp-float", 2)} />

      <Card x={4} y={160} w={196} h={218} z={4} pad={18} {...motion("lp-pop", 0)}>
        <Dir rtl={rtl}>
          <div style={{ width: 50, height: 50, borderRadius: 13, background: "#eef2ff", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke={NAVY} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
              <rect x="5" y="2.5" width="14" height="19" rx="2.5" /><rect x="8" y="5.5" width="8" height="5" rx="1" />
              <path d="M8.5 14h.01M12 14h.01M15.5 14h.01M8.5 17.5h.01M12 17.5h.01M15.5 17.5h.01" />
            </svg>
          </div>
          <div style={{ fontFamily: HEAD, fontSize: 17, fontWeight: 800, marginTop: 14, lineHeight: 1.25 }}>{text.pos}</div>
          <div style={{ fontSize: 12.5, color: SLATE, marginTop: 6, lineHeight: 1.45 }}>{text.posSub}</div>
        </Dir>
      </Card>

      <div className="lp-pop" dir={rtl ? "rtl" : "ltr"} style={at(202, 176, { width: 136, zIndex: 3, textAlign: "center", fontSize: 13, fontWeight: 800, color: BLUE, lineHeight: 1.3, "--d": 3 })}>{text.inLabel}</div>
      <div className="lp-pop" dir={rtl ? "rtl" : "ltr"} style={at(202, 326, { width: 136, zIndex: 3, textAlign: "center", fontSize: 13, fontWeight: 800, color: "#c2410c", lineHeight: 1.3, "--d": 4 })}>{text.outLabel}</div>

      <div className="lp-pop" style={at(342, 120, { width: 214, height: 290, zIndex: 4, borderRadius: 20, padding: 20, color: "#fff", "--d": 2,
        background: `linear-gradient(140deg, ${NAVY} 0%, ${ROYAL} 100%)`, boxShadow: "0 30px 60px -24px rgba(20,34,110,.65)" })}>
        <Dir rtl={rtl}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <img src="/logo.svg" alt="" width={34} height={34} style={{ borderRadius: 9, display: "block", background: "#fff" }} />
            <bdi style={{ fontFamily: HEAD, fontWeight: 800, fontSize: 17 }}>Netrev.ai</bdi>
          </div>
          <div style={{ fontFamily: HEAD, fontSize: 20, fontWeight: 800, marginTop: 16, lineHeight: 1.25 }}>{text.netrev}</div>
          <div style={{ fontSize: 12.5, color: "rgba(255,255,255,.8)", marginTop: 6, lineHeight: 1.45 }}>{text.terms}</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 14 }}>
            {text.chips.map((c) => (
              <span key={c} style={{ fontSize: 11.5, fontWeight: 700, padding: "4px 9px", borderRadius: 999, background: "rgba(255,255,255,.14)", border: "1px solid rgba(255,255,255,.24)" }}>{c}</span>
            ))}
          </div>
        </Dir>
      </div>
    </Canvas>
  );
}

// ════════════════════════════════════════════════════════════════════
// WHY — line icon in a tile on rings, with a check badge that pops in.
// ════════════════════════════════════════════════════════════════════
const RING_ICONS = {
  operators: (
    <>
      <path d="M5 15 8 6h24l3 9" />
      <path d="M5 15a3.75 3.75 0 0 0 7.5 0 3.75 3.75 0 0 0 7.5 0 3.75 3.75 0 0 0 7.5 0 3.75 3.75 0 0 0 7.5 0" />
      <path d="M8 19v15h24V19M17 34v-8h6v8" />
    </>
  ),
  markets: (
    <>
      <circle cx="20" cy="20" r="15" />
      <path d="M5 20h30M20 5c5.5 4.5 5.5 25.5 0 30M20 5c-5.5 4.5-5.5 25.5 0 30" />
    </>
  ),
  app: (
    <>
      <rect x="11" y="4" width="18" height="32" rx="3.5" />
      <path d="M17.5 8h5M16 28v-4M20 28v-8M24 28v-6" />
    </>
  ),
};
export function RingIcon({ kind, size = 150 }) {
  const id = `lp-ring-${kind}`;
  return (
    <svg width={size} height={size} viewBox="0 0 150 150" aria-hidden="true" style={{ display: "block", margin: "0 auto", overflow: "visible" }}>
      <defs>
        <linearGradient id={id} x1="0" y1="1" x2="1" y2="0">
          <stop offset="0" stopColor={NAVY} /><stop offset="1" stopColor="#7d96ff" />
        </linearGradient>
      </defs>
      {[70, 60, 50, 40].map((r, i) => (
        <circle key={r} cx="75" cy="75" r={r} fill={["#f4f6ff", "#e9eeff", "#dfe6ff", "#d3dcfe"][i]} stroke={BLUE} strokeOpacity=".45" strokeWidth="1" />
      ))}
      <rect x="40" y="40" width="70" height="70" rx="17" fill="#fff" stroke={BLUE} strokeWidth="2.5" />
      <g transform="translate(55 55)" fill="none" stroke={BLUE} strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round">
        {RING_ICONS[kind]}
      </g>
      <g className="lp-pop" style={{ transformBox: "fill-box", transformOrigin: "center", "--d": 3 }}>
        <circle cx="111" cy="40" r="17" fill={`url(#${id})`} stroke="#fff" strokeWidth="3.5" />
        <path d="M103.5 40.5l5 5 9.5-10" fill="none" stroke="#fff" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round" />
      </g>
    </svg>
  );
}

// ════════════════════════════════════════════════════════════════════
// OUR STORY — hero composite + one illustration per chapter.
// ════════════════════════════════════════════════════════════════════
export function StoryHeroArt({ label, gap, rtl }) {
  return (
    <Canvas w={560} h={500} label={label}>
      <Orb x={170} y={60} d={380} />
      <div className="lp-pop lp-float lp-par" style={at(20, 150, { zIndex: 3, "--d": 1, "--depth": 10 })}>
        <div style={{ width: 200, height: 200, borderRadius: 36, background: "#fff", boxShadow: "0 34px 64px -28px rgba(15,27,77,.45), 0 0 0 1px rgba(15,27,77,.06)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <RingIcon kind="operators" size={178} />
        </div>
      </div>
      <GapCard x={272} y={116} w={260} gap={gap} rtl={rtl} d={3} depth={16} k={1.08} />
      <Tile p="deliveroo" x={104} y={40} size={72} {...motion("lp-pop lp-float lp-par", 2, 26)} />
      <Tile p="talabat" x={438} y={30} size={66} {...motion("lp-pop lp-float lp-par", 4, 22)} />
      <Tile p="careem" x={196} y={400} size={64} {...motion("lp-pop lp-float lp-par", 5, 24)} />
      <Tile p="keeta" x={466} y={404} size={62} {...motion("lp-pop lp-float lp-par", 6, 20)} />
    </Canvas>
  );
}

function Sparkline({ up, color }) {
  const pts = up ? "0,40 20,36 40,30 60,31 80,20 100,14 120,6" : "0,8 20,12 40,10 60,20 80,24 100,32 120,38";
  return (
    <svg width="160" height="46" viewBox="0 0 120 46" preserveAspectRatio="none" style={{ display: "block", overflow: "visible" }}>
      <polyline points={pts} fill="none" stroke={color} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

// kind: busy | kitchens | clicked | sharing. `text` holds translated labels.
export function ChapterArt({ kind, label, text, rtl }) {
  return (
    <Canvas w={520} h={420} label={label}>
      <Rings cx={260} cy={214} r={196} />

      {kind === "busy" && (
        <>
          <Win x={40} y={70} w={440} h={250} title="Daily Log" sub="bistro47 · Orders per day" active={2} {...motion("lp-pop", 0)}>
            <div style={{ display: "flex", alignItems: "flex-end", gap: 12, height: 130, padding: "10px 8px 0", background: "#fff", border: `1px solid ${LINE}`, borderRadius: 10 }}>
              {[34, 46, 52, 63, 71, 84, 97].map((h, i) => (
                <div key={h} className="lp-pop" style={{ flex: 1, height: `${h}%`, borderRadius: "6px 6px 0 0", background: `linear-gradient(180deg, ${ROYAL}, ${NAVY})`, transformOrigin: "bottom", "--d": i + 2 }} />
              ))}
            </div>
          </Win>
          <Card x={290} y={14} w={200} h={100} z={6} pad={14} {...motion("lp-pop lp-float", 3)}>
            <Dir rtl={rtl} style={{ fontSize: 12, fontWeight: 800, color: "#15803d", marginBottom: 6 }}>{text.orders} ↑</Dir>
            <Sparkline up color="#16a34a" />
          </Card>
          <Card x={20} y={300} w={200} h={100} z={6} pad={14} {...motion("lp-pop lp-float", 5)}>
            <Dir rtl={rtl} style={{ fontSize: 12, fontWeight: 800, color: "#b91c1c", marginBottom: 6 }}>{text.profit} ↓</Dir>
            <Sparkline color="#dc2626" />
          </Card>
          <Tile p="deliveroo" x={424} y={300} size={64} {...motion("lp-pop lp-float", 6)} />
        </>
      )}

      {kind === "kitchens" && (
        <>
          <svg style={at(0, 0, { zIndex: 1 })} width={520} height={420}>
            {[86, 196, 306].map((x) => (
              <path key={x} d={`M${x + 32} 108 C${x + 32} 150, 260 150, 260 176`} stroke={BLUE} strokeWidth={2.2} strokeDasharray="4 4" fill="none" className="lp-flow" />
            ))}
          </svg>
          {[["A", "#ff7a45"], ["B", "#1dbf73"], ["C", "#7c5cff"]].map(([b, c], i) => (
            <div key={b} className="lp-pop lp-float" style={at(86 + i * 110, 40, { width: 64, height: 64, borderRadius: 16, background: c, border: "4px solid #fff", zIndex: 3, "--d": i + 1,
              boxShadow: "0 14px 28px -12px rgba(15,27,77,.45)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontFamily: HEAD, fontWeight: 800, fontSize: 24 })}>{b}</div>
          ))}
          <Card x={110} y={176} w={300} h={176} z={4} pad={14} {...motion("lp-pop", 4)}>
            <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr 1fr", gap: 5 }}>
              {Array.from({ length: 12 }, (_, i) => (
                <div key={i} style={{ height: 22, borderRadius: 4, background: i < 3 ? "#e8edff" : "#f3f5fa" }} />
              ))}
              <div style={{ height: 26, borderRadius: 5, background: "#fff1f2", border: "1.5px dashed #f87171", gridColumn: "1 / -1", display: "flex", alignItems: "center", justifyContent: "center", color: "#dc2626", fontFamily: HEAD, fontWeight: 800, fontSize: 16 }}>?</div>
            </div>
          </Card>
          <div className="lp-pop" style={at(312, 332, { zIndex: 6, "--d": 6 })}>
            <Chip tone="slate" style={{ fontSize: 13, padding: "7px 14px", boxShadow: "0 10px 20px -12px rgba(15,27,77,.4)" }}><span dir={rtl ? "rtl" : "ltr"}>{text.breakeven}</span></Chip>
          </div>
        </>
      )}

      {kind === "clicked" && (
        <>
          {[{ y: 70, ok: true, item: text.itemA, val: "+18%" }, { y: 222, ok: false, item: text.itemB, val: "−9%" }].map((r, i) => (
            <Card key={r.val} x={90} y={r.y} w={340} h={128} z={4} pad={16} {...motion("lp-pop", i * 2 + 1)} style={{ borderInlineStart: `5px solid ${r.ok ? "#16a34a" : "#dc2626"}` }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Chip tone="orange">50% off</Chip>
                <span style={{ width: 30, height: 30, borderRadius: "50%", background: r.ok ? "#16a34a" : "#dc2626", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {r.ok ? <Tick size={15} color="#fff" /> : <Cross size={14} color="#fff" />}
                </span>
              </div>
              <Dir rtl={rtl} style={{ fontFamily: HEAD, fontSize: 18, fontWeight: 800, marginTop: 10 }}>{r.item}</Dir>
              <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginTop: 4 }}>
                <span style={{ fontFamily: HEAD, fontSize: 24, fontWeight: 800, color: r.ok ? "#15803d" : "#dc2626" }}>{r.val}</span>
                <span dir={rtl ? "rtl" : "ltr"} style={{ fontSize: 12, color: SLATE, fontWeight: 600 }}>{text.margin}</span>
              </div>
            </Card>
          ))}
          <div className="lp-pop lp-float" style={at(400, 30, { zIndex: 6, "--d": 5 })}>
            <div style={{ width: 66, height: 66, borderRadius: 18, background: `linear-gradient(135deg, ${NAVY}, ${ROYAL})`, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 16px 30px -12px rgba(20,34,110,.7)", border: "4px solid #fff" }}>
              <Spark size={26} color="#fff" />
            </div>
          </div>
        </>
      )}

      {kind === "sharing" && (
        <>
          <div className="lp-pop" style={at(170, 120, { zIndex: 4, "--d": 1 })}>
            <div style={{ width: 180, height: 180, borderRadius: 40, background: `linear-gradient(140deg, ${NAVY}, ${ROYAL})`, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 34px 60px -24px rgba(20,34,110,.7)", border: "6px solid #fff" }}>
              <img src="/logo.svg" alt="" width={92} height={92} style={{ borderRadius: 22, display: "block", background: "#fff" }} />
            </div>
          </div>
          {[["UK", "£", 40, 70], ["UAE", "AED", 360, 64], ["India", "₹", 30, 300], ["USA", "$", 372, 296]].map(([c, s, x, y], i) => (
            <div key={c} className="lp-pop lp-float" style={at(x, y, { zIndex: 5, "--d": i + 2 })}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, background: "#fff", borderRadius: 999, padding: "9px 16px 9px 9px", boxShadow: "0 16px 30px -14px rgba(15,27,77,.45), 0 0 0 1px rgba(15,27,77,.06)" }}>
                <span style={{ minWidth: 32, height: 32, padding: "0 7px", borderRadius: 999, background: "#eef2ff", color: BLUE, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 12.5 }}>{s}</span>
                <span style={{ fontFamily: HEAD, fontWeight: 800, fontSize: 16 }}>{c}</span>
              </div>
            </div>
          ))}
        </>
      )}
    </Canvas>
  );
}
